import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Pencil } from "lucide-react";
import { useCanMutate } from "@/lib/rbac";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

interface EditableCellProps {
  value: string;
  displayValue?: string; // Shown in non-editing mode (e.g., LinkedIn handle instead of full URL)
  onChange: (value: string) => void;
  className?: string;
  children?: React.ReactNode;
  cellId?: string; // unique identifier like "recordId:fieldKey" for programmatic edit triggering
  validate?: (value: string) => string | null; // Returns error message or null if valid
  // Fires while the inline input or hover editor is open. The hover editor renders
  // inline (not portaled), so a sticky parent <td> uses this to raise its z-index
  // above later rows' sticky cells, which would otherwise paint over it.
  onEditingChange?: (editing: boolean) => void;
}

/** Payload of the window "restoreCellValue" event (see FieldHistoryDialog). */
export interface RestoreCellDetail {
  cellId: string;
  value: string;
  /** Only ask whether a cell would handle it — no write. */
  probe?: boolean;
  handled?: boolean;
}

// Empty grid cells display a dash placeholder (the app-wide "-" / "—"
// convention). It's a placeholder, not real data, so an edit should start
// from a blank field rather than making the user delete the dash first.
const isPlaceholder = (v: string) => v === "-" || v === "—";
const stripPlaceholder = (v: string) => (isPlaceholder(v.trim()) ? "" : v);

export default function EditableCell({ value, displayValue, onChange, className, children, cellId, validate, onEditingChange }: EditableCellProps) {
  const canMutate = useCanMutate();
  const { t } = useTranslation();
  // Backend data can be non-string (numeric/array customProperties); draft and
  // hoverDraft get .trim()/.split() called on them, so coerce once up front.
  const stringValue = value == null ? "" : String(value);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => stripPlaceholder(stringValue));
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [hoverDraft, setHoverDraft] = useState(stringValue);
  const hoverTextareaRef = useRef<HTMLTextAreaElement>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  // Decide whether the popover should open above or below the trigger based
  // on the sticky <thead>'s bottom edge. If the trigger sits close to (or
  // under) the sticky header, opening upward would cover the header and the
  // summary tiles above it — flip downward instead.
  const [preferredSide, setPreferredSide] = useState<"top" | "bottom">("bottom");
  useEffect(() => {
    if (!hoverOpen) return;
    const trigger = triggerRef.current;
    if (!trigger) return;
    const triggerTop = trigger.getBoundingClientRect().top;
    const thead = trigger.closest("table")?.querySelector("thead");
    const theadBottom = thead?.getBoundingClientRect().bottom ?? 0;
    // ~200px is roughly the tallest popover we render; if there isn't that
    // much room between the sticky header and the trigger, open downward.
    setPreferredSide(triggerTop - theadBottom < 220 ? "bottom" : "top");
  }, [hoverOpen]);

  useEffect(() => {
    if (hoverOpen) setHoverDraft(stringValue);
  }, [hoverOpen, stringValue]);

  const isOpen = editing || hoverOpen;
  useEffect(() => {
    onEditingChange?.(isOpen);
  }, [isOpen, onEditingChange]);

  useEffect(() => {
    if (editing) {
      setDraft(stripPlaceholder(stringValue));
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [editing, stringValue]);

  // Listen for programmatic edit trigger via custom event. Read-only
  // users still receive the event but are not allowed to enter edit mode.
  useEffect(() => {
    if (!cellId || !canMutate) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail === cellId) {
        setEditing(true);
      }
    };
    window.addEventListener("triggerCellEdit", handler);
    return () => window.removeEventListener("triggerCellEdit", handler);
  }, [cellId, canMutate]);

  // Programmatic value restore (e.g. rolling back from Field History). Marks
  // the event handled so the caller knows the save path was reached; a probe
  // only asks whether this cell would take it. The first matching cell wins.
  useEffect(() => {
    if (!cellId || !canMutate) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<RestoreCellDetail>).detail;
      if (detail?.cellId !== cellId || detail.handled) return;
      detail.handled = true;
      if (detail.probe) return;
      if (detail.value !== stripPlaceholder(stringValue)) onChange(detail.value);
    };
    window.addEventListener("restoreCellValue", handler);
    return () => window.removeEventListener("restoreCellValue", handler);
  }, [cellId, canMutate, stringValue, onChange]);

  const commit = () => {
    const trimmed = draft.trim();

    // Run validation if provided
    if (validate && trimmed) {
      const validationError = validate(trimmed);
      if (validationError) {
        setError(validationError);
        return; // Don't commit if validation fails
      }
    }

    setError(null);
    setEditing(false);
    // Compare against the placeholder-stripped value so re-committing an
    // untouched empty cell doesn't fire a spurious onChange("").
    if (trimmed !== stripPlaceholder(stringValue)) {
      onChange(trimmed);
    }
  };

  const commitHover = () => {
    const trimmed = hoverDraft.trim();
    if (validate && trimmed) {
      const validationError = validate(trimmed);
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    setError(null);
    if (trimmed !== stringValue) onChange(trimmed);
  };

  if (editing) {
    return (
      <div className="relative">
        <input
          ref={inputRef}
          value={draft}
          onChange={e => {
            setDraft(e.target.value);
            setError(null); // Clear error when user types
          }}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") { setDraft(stringValue); setEditing(false); setError(null); }
          }}
          className={cn(
            "w-full bg-transparent border-b outline-none text-sm py-0 px-0",
            error ? "border-destructive" : "border-accent",
            className
          )}
        />
        {error && (
          <div className="absolute left-0 top-full mt-1 text-xs text-destructive whitespace-nowrap z-50">
            {error}
          </div>
        )}
      </div>
    );
  }

  // Read-only users see the value with no edit affordance — no pencil,
  // no double-click handler, no hover ring. Mutation hooks still guard
  // the actual write as a final safety net.
  if (!canMutate) {
    return (
      <HoverCard openDelay={400} closeDelay={100} open={hoverOpen} onOpenChange={setHoverOpen}>
        <HoverCardTrigger asChild>
          <span
            ref={triggerRef}
            className={cn("flex items-center gap-1 min-w-0 w-full", className)}
            title={t("common.readOnlyCannotChange")}
          >
            <span className="truncate min-w-0 flex-1" onClick={e => { e.stopPropagation(); }}>
              {children || displayValue || value}
            </span>
          </span>
        </HoverCardTrigger>
        {value ? (
          <HoverCardContent className="w-80 max-h-64 overflow-y-auto text-sm" side={preferredSide} align="start" collisionPadding={8}>
            <p className="whitespace-pre-wrap break-words text-foreground select-text">{value}</p>
          </HoverCardContent>
        ) : null}
      </HoverCard>
    );
  }

  return (
    <HoverCard openDelay={400} closeDelay={150} open={hoverOpen} onOpenChange={setHoverOpen}>
      <HoverCardTrigger asChild>
        <span
          ref={triggerRef}
          className={cn("flex items-center gap-1 min-w-0 w-full group/edit cursor-text hover:bg-muted/50 rounded px-1 -mx-1 py-0.5 -my-0.5 transition", className)}
          onDoubleClick={e => { e.stopPropagation(); setEditing(true); }}
        >
          <span className="truncate min-w-0 flex-1" onClick={e => { e.stopPropagation(); }}>
            {children || displayValue || value}
          </span>
          <Pencil
            className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover/name:opacity-100 group-hover/edit:opacity-100 transition shrink-0"
            onClick={e => { e.stopPropagation(); setEditing(true); }}
          />
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        className="w-80 text-sm p-2 space-y-2"
        side={preferredSide}
        align="start"
        collisionPadding={8}
        onClick={e => e.stopPropagation()}
      >
        <textarea
          ref={hoverTextareaRef}
          value={hoverDraft}
          onChange={e => { setHoverDraft(e.target.value); setError(null); }}
          onBlur={commitHover}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              commitHover();
              setHoverOpen(false);
            }
            if (e.key === "Escape") { setHoverDraft(stringValue); setError(null); setHoverOpen(false); }
          }}
          rows={Math.min(8, Math.max(2, hoverDraft.split("\n").length))}
          className={cn(
            "w-full resize-y rounded-md border bg-background px-2 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/20",
            error ? "border-destructive" : "border-input"
          )}
        />
        {error && <div className="text-xs text-destructive">{error}</div>}
        <div className="text-[11px] text-muted-foreground">{t("grid.cell.editorHint")}</div>
      </HoverCardContent>
    </HoverCard>
  );
}
